import {
  ExperienceConfigurationAnonymousResponse,
  ExperienceConfigurationAnonymousResponseSchema,
  ExperienceConfigurationResponse,
  ExperienceConfigurationResponseSchema,
} from '@monetize/types/experience-config';
import { UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import {
  useGetAnonymousExperienceConfiguration,
  useGetExperienceConfiguration,
} from '../api/experienceConfigurationService';

const getDefaultValue = <K extends keyof ExperienceConfigurationResponse>(
  key: K,
) => {
  return ExperienceConfigurationResponseSchema.shape[key].parse(
    {},
  ) as ExperienceConfigurationResponse[K];
};

/** The anonymous schema is narrower (e.g. quoteAndContractSettings is a pick),
 * so anonymous defaults must come from the anonymous contract. */
const getAnonymousDefaultValue = <
  K extends keyof ExperienceConfigurationAnonymousResponse,
>(
  key: K,
) => {
  return ExperienceConfigurationAnonymousResponseSchema.shape[key].parse(
    {},
  ) as ExperienceConfigurationAnonymousResponse[K];
};

export function useExperienceConfiguration<
  K extends keyof ExperienceConfigurationResponse,
>(
  key: K,
  options: Omit<
    Partial<
      UseQueryOptions<
        ExperienceConfigurationResponse,
        unknown,
        ExperienceConfigurationResponse[K]
      >
    >,
    'select'
  > = {},
) {
  const defaultValue = useMemo(() => getDefaultValue(key), [key]);
  const select = useCallback(
    (value: ExperienceConfigurationResponse) => value[key],
    [key],
  );
  const { data } = useGetExperienceConfiguration({
    meta: { showErrorToast: false },
    ...options,
    select,
  });

  return data ?? defaultValue;
}

export function useAnonymousExperienceConfiguration<
  Key extends keyof ExperienceConfigurationAnonymousResponse,
>(tenantId: string, key: Key) {
  const defaultData = useMemo(() => getAnonymousDefaultValue(key), [key]);
  const { data } = useGetAnonymousExperienceConfiguration(tenantId, {
    meta: { showErrorToast: false },
    select: (value) => value[key],
  });

  return data ?? defaultData;
}
